<template>
  <div class="settings-feedback" data-testid="settings-feedback">
    <!-- Success state -->
    <div v-if="isSuccess" class="sf-success">
      <div class="sf-success-icon">
        <i class="pi pi-check"></i>
      </div>
      <p class="sf-success-title">{{ STRINGS.feedback.success.title }}</p>
      <p class="sf-success-message">{{ STRINGS.feedback.success.message }}</p>
      <button class="sf-reset-btn" @click="resetForm">
        {{ STRINGS.feedback.buttons.close }}
      </button>
    </div>

    <!-- Form -->
    <template v-else>
      <div :class="{ 'sf-submitting': isSubmitting }">
        <p class="sf-prompt">{{ STRINGS.feedback.prompt }}</p>

        <!-- Type selector -->
        <div class="sf-types" :aria-label="STRINGS.aria.labels.feedbackType" role="radiogroup">
          <button
            v-for="opt in categoryOptions"
            :key="opt.value"
            class="sf-type"
            :class="{ 'sf-type--active': category === opt.value }"
            role="radio"
            :aria-checked="category === opt.value"
            @click="category = opt.value"
          >
            <i :class="opt.icon"></i>
            <span>{{ opt.label }}</span>
          </button>
        </div>

        <!-- Subject -->
        <div class="sf-field">
          <label class="sf-label">{{ STRINGS.feedback.labels.subject }}</label>
          <input
            v-model="subject"
            class="sf-input"
            :class="{ 'sf-input--error': hasAttemptedSubmit && !subject.trim() }"
            type="text"
            :placeholder="subjectPlaceholder"
          />
          <span v-if="hasAttemptedSubmit && !subject.trim()" class="sf-error">
            {{ STRINGS.feedback.errors.subjectRequired }}
          </span>
        </div>

        <!-- Description -->
        <div class="sf-field">
          <label class="sf-label">{{ STRINGS.feedback.labels.description }}</label>
          <textarea
            v-model="description"
            class="sf-input sf-textarea"
            :class="{ 'sf-input--error': hasAttemptedSubmit && !description.trim() }"
            :placeholder="descriptionPlaceholder"
          ></textarea>
          <span v-if="hasAttemptedSubmit && !description.trim()" class="sf-error">
            {{ STRINGS.feedback.errors.descriptionRequired }}
          </span>
        </div>

        <!-- Contact consent -->
        <label class="sf-consent">
          <input v-model="contactOk" type="checkbox" class="sf-checkbox" />
          <span class="sf-consent-label">{{ STRINGS.feedback.labels.contactOk }}</span>
        </label>

        <!-- Server error -->
        <p v-if="error" class="sf-error">{{ error.message }}</p>

        <!-- Submit -->
        <button
          class="sf-submit"
          :class="{ 'sf-submit--loading': isSubmitting }"
          :disabled="isSubmitting"
          @click="handleSubmit"
        >
          <i v-if="!isSubmitting" class="pi pi-send sf-send-icon"></i>
          {{ isSubmitting ? STRINGS.feedback.buttons.sending : STRINGS.feedback.buttons.submit }}
        </button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import STRINGS from '@/constants/strings';
import { useFeedback } from '@/composables/useFeedback';
import type { FeedbackCategory } from '@/types/feedback';

const props = defineProps<{
  visible: boolean;
}>();

const { isSubmitting, error, isSuccess, submitFeedback, reset } = useFeedback();

const category = ref<FeedbackCategory>('bug');
const subject = ref('');
const description = ref('');
const contactOk = ref(false);
const hasAttemptedSubmit = ref(false);

const categoryOptions = [
  {
    value: 'bug' as FeedbackCategory,
    label: STRINGS.feedback.categories.bug,
    icon: 'pi pi-exclamation-circle',
  },
  {
    value: 'idea' as FeedbackCategory,
    label: STRINGS.feedback.categories.idea,
    icon: 'pi pi-lightbulb',
  },
  {
    value: 'other' as FeedbackCategory,
    label: STRINGS.feedback.categories.other,
    icon: 'pi pi-comments',
  },
];

const subjectPlaceholder = computed(() => {
  const map: Record<FeedbackCategory, string> = {
    bug: STRINGS.feedback.placeholders.bugSubject,
    idea: STRINGS.feedback.placeholders.ideaSubject,
    other: STRINGS.feedback.placeholders.otherSubject,
  };
  return map[category.value];
});

const descriptionPlaceholder = computed(() => {
  const map: Record<FeedbackCategory, string> = {
    bug: STRINGS.feedback.placeholders.bugDescription,
    idea: STRINGS.feedback.placeholders.ideaDescription,
    other: STRINGS.feedback.placeholders.otherDescription,
  };
  return map[category.value];
});

function resetForm(): void {
  category.value = 'bug';
  subject.value = '';
  description.value = '';
  contactOk.value = false;
  hasAttemptedSubmit.value = false;
  reset();
}

async function handleSubmit(): Promise<void> {
  hasAttemptedSubmit.value = true;

  if (!subject.value.trim() || !description.value.trim()) {
    return;
  }

  await submitFeedback({
    category: category.value,
    subject: subject.value.trim(),
    description: description.value.trim(),
    ...(contactOk.value ? { contact_ok: true } : {}),
  });
}

// Reset form when section becomes visible
watch(
  () => props.visible,
  newVal => {
    if (newVal) {
      resetForm();
    }
  }
);
</script>

<style scoped>
.settings-feedback {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.sf-submitting {
  opacity: 0.5;
  pointer-events: none;
}

.sf-prompt {
  font-size: 0.8125rem;
  color: var(--color-arena-text-muted);
  line-height: 1.5;
  margin: 0 0 1.25rem;
}

/* Type selector */
.sf-types {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
}

.sf-type {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
  padding: 0.75rem 0.5rem;
  background: transparent;
  border: 1px solid var(--color-arena-border);
  border-radius: var(--radius-input);
  color: var(--color-arena-text-muted);
  cursor: pointer;
  transition:
    border-color 150ms ease,
    background-color 150ms ease,
    color 150ms ease;
  font-family: var(--font-sans);
}

.sf-type:hover {
  border-color: var(--color-arena-text-dim);
  color: var(--color-arena-text-bright);
}

.sf-type--active {
  border-color: var(--color-energy-primary);
  background: rgba(201, 168, 76, 0.06);
  color: var(--color-energy-primary);
}

.sf-type i {
  font-size: 1.125rem;
}

.sf-type span {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

/* Form fields */
.sf-field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  margin-bottom: 1rem;
}

.sf-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-arena-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.sf-input {
  font-family: var(--font-sans);
  font-size: 0.875rem;
  color: var(--color-arena-text-bright);
  background: var(--color-arena-void);
  border: 1px solid var(--color-arena-border);
  border-radius: var(--radius-input);
  padding: 0.625rem 0.75rem;
  outline: none;
  transition: border-color 150ms ease;
}

.sf-input:focus {
  border-color: var(--color-energy-primary);
  box-shadow: 0 0 0 2px rgba(201, 168, 76, 0.15);
}

.sf-input::placeholder {
  color: var(--color-arena-text-dim);
}

.sf-textarea {
  resize: vertical;
  min-height: 100px;
  line-height: 1.5;
}

.sf-input--error {
  border-color: var(--color-energy-danger);
}

.sf-error {
  font-size: 0.6875rem;
  color: var(--color-energy-danger);
}

/* Consent */
.sf-consent {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  margin-bottom: 1rem;
}

.sf-checkbox {
  appearance: none;
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  border: 1px solid var(--color-arena-border);
  border-radius: 3px;
  background: var(--color-arena-void);
  cursor: pointer;
  position: relative;
  flex-shrink: 0;
  transition:
    border-color 150ms ease,
    background 150ms ease;
}

.sf-checkbox:checked {
  background: var(--color-energy-primary);
  border-color: var(--color-energy-primary);
}

.sf-checkbox:checked::after {
  content: '';
  position: absolute;
  left: 4px;
  top: 1px;
  width: 5px;
  height: 9px;
  border: solid var(--color-arena-void);
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.sf-consent-label {
  font-size: 0.8125rem;
  color: var(--color-arena-text-muted);
}

/* Submit */
.sf-submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.625rem 1.25rem;
  min-height: 44px;
  font-family: var(--font-sans);
  font-size: 0.8125rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-radius: var(--radius-button);
  cursor: pointer;
  transition:
    background-color 150ms ease,
    opacity 150ms ease;
  background: linear-gradient(135deg, var(--color-primary-600), var(--color-primary-500));
  border: none;
  color: var(--color-arena-void);
}

.sf-submit:hover:not(:disabled) {
  background: linear-gradient(135deg, var(--color-primary-700), var(--color-primary-600));
}

.sf-submit:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.sf-send-icon {
  font-size: 0.75rem;
}

.sf-submit--loading {
  position: relative;
  color: transparent;
}

.sf-submit--loading::after {
  content: '';
  position: absolute;
  width: 16px;
  height: 16px;
  border: 2px solid var(--color-arena-void);
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 600ms linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Success */
.sf-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  text-align: center;
  padding: 2rem 0;
}

.sf-success-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(0, 196, 143, 0.1);
  border: 2px solid rgba(0, 196, 143, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: var(--color-energy-success);
}

.sf-success-title {
  font-family: var(--font-display);
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--color-arena-text-bright);
  letter-spacing: 0.04em;
  margin: 0;
}

.sf-success-message {
  font-size: 0.8125rem;
  color: var(--color-arena-text-muted);
  line-height: 1.6;
  max-width: 300px;
  margin: 0;
}

.sf-reset-btn {
  background: transparent;
  border: none;
  color: var(--color-arena-text-muted);
  font-family: var(--font-sans);
  font-size: 0.8125rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: var(--radius-button);
  transition: color 150ms ease;
  margin-top: 0.5rem;
}

.sf-reset-btn:hover {
  color: var(--color-arena-text-bright);
}

@media (prefers-reduced-motion: reduce) {
  .sf-submit--loading::after {
    animation: none;
  }
}
</style>
